import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { projectsAPI, departmentsAPI, usersAPI } from '../api/client';

export default function ProjectsSettings() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [formData, setFormData] = useState({
    projectCode: '',
    projectName: '',
    description: '',
    departmentId: '',
    projectManagerId: '',
    startDate: '',
    endDate: '',
    budget: '',
    status: 'Active'
  });

  const statuses = ['Active', 'OnHold', 'Completed', 'Cancelled'];

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronUp className="w-3 h-3 text-gray-300" />;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3 text-blue-600" /> : <ChevronDown className="w-3 h-3 text-blue-600" />;
  };

  const sortedProjects = [...projects].sort((a, b) => {
    if (!sortField) return 0;
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortField === 'Budget') {
      return dir * ((parseFloat(a.Budget) || 0) - (parseFloat(b.Budget) || 0));
    }
    const aVal = (a[sortField] || '').toString();
    const bVal = (b[sortField] || '').toString();
    return dir * aVal.localeCompare(bVal);
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [projectsRes, deptsRes, usersRes] = await Promise.all([
        projectsAPI.getAll(),
        departmentsAPI.getAll(),
        usersAPI.getAll()
      ]);

      setProjects(Array.isArray(projectsRes) ? projectsRes : (projectsRes.data?.projects || projectsRes.data || []));
      
      // Ensure departments is always an array
      let depts = [];
      if (Array.isArray(deptsRes)) {
        depts = deptsRes;
      } else if (deptsRes?.data) {
        depts = Array.isArray(deptsRes.data) ? deptsRes.data : [];
      }
      setDepartments(depts);
      
      setUsers(Array.isArray(usersRes) ? usersRes : (usersRes.data?.users || []));
    } catch (error) {
      console.error('Fetch data error:', error);
      alert('Failed to load data');
      // Ensure states are arrays even on error
      setDepartments([]);
      setProjects([]);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingProject(null);
    setFormData({
      projectCode: '',
      projectName: '',
      description: '',
      departmentId: '',
      projectManagerId: '',
      startDate: '',
      endDate: '',
      budget: '',
      status: 'Active'
    });
    setShowModal(true);
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      projectCode: project.ProjectCode,
      projectName: project.ProjectName,
      description: project.Description || '',
      departmentId: project.DepartmentID || '',
      projectManagerId: project.ProjectManagerID || '',
      startDate: project.StartDate ? project.StartDate.split('T')[0] : '',
      endDate: project.EndDate ? project.EndDate.split('T')[0] : '',
      budget: project.Budget || '',
      status: project.Status
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingProject) {
        await projectsAPI.update(editingProject.ProjectID, {
          ...formData,
          isActive: true
        });
        alert('Project updated successfully');
      } else {
        await projectsAPI.create(formData);
        alert('Project created successfully');
      }

      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error('Save project error:', error);
      alert(error.response?.data?.message || 'Failed to save project');
    }
  };

  const handleDeactivate = async (projectId) => {
    if (!confirm('Deactivate this project?')) return;

    try {
      await projectsAPI.delete(projectId);
      alert('Project deactivated successfully');
      fetchData();
    } catch (error) {
      console.error('Deactivate project error:', error);
      alert(error.response?.data?.message || 'Failed to deactivate project');
    }
  };

  const handleImportFile = async (e) => {
    e.preventDefault();

    if (!importFile) {
      alert('Please select a file to import');
      return;
    }

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', importFile);

      const response = await projectsAPI.importExcel(formDataUpload);
      
      alert(`Import completed!\nImported: ${response.imported}\nSkipped: ${response.skipped}${response.errors ? '\n\nErrors:\n' + response.errors.join('\n') : ''}`);
      
      setImportFile(null);
      fetchData();
    } catch (error) {
      console.error('Import error:', error);
      alert(error.response?.data?.message || 'Failed to import projects');
    }
  };

  const downloadTemplate = () => {
    const template = [
      ['Project Code', 'Project Name', 'Description', 'Start Date', 'End Date', 'Budget', 'Status'],
      ['PROJ-001', 'Website Redesign', 'Complete website redesign project', '2026-01-01', '2026-06-30', '50000', 'Active'],
      ['PROJ-002', 'Mobile App', 'New mobile application development', '2026-02-01', '2026-12-31', '100000', 'Active']
    ];

    const csvContent = template.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'projects-template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Active': 'bg-green-100 text-green-800',
      'OnHold': 'bg-yellow-100 text-yellow-800',
      'Completed': 'bg-blue-100 text-blue-800',
      'Cancelled': 'bg-gray-100 text-gray-800'
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Projects</h2>
          <p className="text-sm text-gray-600 mt-1">Manage company projects and budgets</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={downloadTemplate}
            className="btn btn-secondary"
          >
            📥 Download Template
          </button>
          <button
            onClick={handleAdd}
            className="btn btn-primary"
          >
            + Add Project
          </button>
        </div>
      </div>

      {/* Import Section */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Import from Excel</h3>
        <form onSubmit={handleImportFile} className="flex items-end gap-3">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Excel File
            </label>
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => setImportFile(e.target.files[0])}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font:semibold
                file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100"
            />
          </div>
          <button
            type="submit"
            className="btn btn-success"
            disabled={!importFile}
          >
            📤 Import
          </button>
        </form>
      </div>

      {/* Projects Table */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('ProjectCode')}><div className="flex items-center gap-1">Code<SortIcon field="ProjectCode" /></div></th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('ProjectName')}><div className="flex items-center gap-1">Project Name<SortIcon field="ProjectName" /></div></th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('DepartmentName')}><div className="flex items-center gap-1">Department<SortIcon field="DepartmentName" /></div></th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('ProjectManagerName')}><div className="flex items-center gap-1">Manager<SortIcon field="ProjectManagerName" /></div></th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('Budget')}><div className="flex items-center justify-end gap-1">Budget<SortIcon field="Budget" /></div></th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('Status')}><div className="flex items-center justify-center gap-1">Status<SortIcon field="Status" /></div></th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sortedProjects.map(project => (
                <tr key={project.ProjectID} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {project.ProjectCode}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    <div>{project.ProjectName}</div>
                    {project.Description && (
                      <div className="text-xs text-gray-500 mt-1">{project.Description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {project.DepartmentName || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {project.ProjectManagerName || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
                    {project.Budget ? `£${parseFloat(project.Budget).toLocaleString('en-GB', { minimumFractionDigits: 2 })}` : '-'}
                  </td>
                  <td className="px-4 py-3 text-center text-sm">
                    {getStatusBadge(project.Status)}
                  </td>
                  <td className="px-4 py-3 text-center text-sm">
                    <button
                      onClick={() => navigate(`/project-lines?projectId=${project.ProjectID}`)}
                      className="text-green-600 hover:text-green-700 px-2 py-1"
                      title="View Lines"
                    >
                      📊
                    </button>
                    <button
                      onClick={() => handleEdit(project)}
                      className="text-blue-600 hover:text-blue-700 px-2 py-1"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDeactivate(project.ProjectID)}
                      className="text-red-600 hover:text-red-700 px-2 py-1 ml-2"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {projects.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No projects found. Click "Add Project" or import from Excel to get started.
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {editingProject ? 'Edit Project' : 'Add Project'}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Project Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.projectCode}
                    onChange={(e) => setFormData({ ...formData, projectCode: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {statuses.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Project Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.projectName}
                  onChange={(e) => setFormData({ ...formData, projectName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Department
                  </label>
                  <select
                    value={formData.departmentId}
                    onChange={(e) => setFormData({ ...formData, departmentId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">None</option>
                    {(departments || []).map(dept => (
                      <option key={dept.DepartmentID} value={dept.DepartmentID}>
                        {dept.Name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Project Manager
                  </label>
                  <select
                    value={formData.projectManagerId}
                    onChange={(e) => setFormData({ ...formData, projectManagerId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">None</option>
                    {(users || []).map(user => (
                      <option key={user.UserID} value={user.UserID}>
                        {user.FirstName} {user.LastName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Budget
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.budget}
                  onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  {editingProject ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
